/**
 * D-124: a student's oral credits in their record: the journal (new period, examiner sessions,
 * credits added) and, for the owner and the manager, credits added as a commercial gesture with
 * a required reason (journal + audit log).
 */

import { getAcademyOralCreditsListQueryKey, useAcademyOralCreditsCreate, useAcademyOralCreditsList } from "@agencyos/api-client";
import { Button, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { dateTimeText } from "../billing/shared";
import { ErrorList, useAcademyRoles } from "./shared";

export function OralCreditsAdmin({ learnerId }: { learnerId: string }) {
  const { t, i18n } = useTranslation();
  const { canEditSettings } = useAcademyRoles();
  const queryClient = useQueryClient();
  const journal = useAcademyOralCreditsList({ learner: learnerId });
  const add = useAcademyOralCreditsCreate({
    mutation: {
      onSuccess: () => {
        setNote("");
        void queryClient.invalidateQueries({ queryKey: getAcademyOralCreditsListQueryKey({ learner: learnerId }) });
      },
    },
  });
  const [amount, setAmount] = useState("1");
  const [note, setNote] = useState("");
  const rows = journal.data?.status === 200 ? journal.data.data.results : [];
  const balance = rows[0]?.balance;

  return (
    <section className="stack" aria-labelledby={`oral-credits-${learnerId}`}>
      <h3 id={`oral-credits-${learnerId}`}>{t("promises.credits.admin.title")}</h3>
      {balance !== undefined ? <p>{t("promises.credits.admin.balance", { count: balance })}</p> : null}
      {canEditSettings ? (
        <form
          className="form-grid"
          onSubmit={(event) => {
            event.preventDefault();
            add.mutate({ data: { learner: learnerId, amount: Number(amount), note } });
          }}
        >
          <TextField label={t("promises.credits.admin.amount")} type="number" min={1} max={100} value={amount} onChange={(e) => setAmount(e.target.value)} />
          <TextField label={t("promises.credits.admin.note")} value={note} required onChange={(e) => setNote(e.target.value)} />
          <div>
            <Button type="submit" size="sm" loading={add.isPending} disabled={!note.trim()}>
              {t("promises.credits.admin.add")}
            </Button>
          </div>
        </form>
      ) : null}
      {add.isSuccess ? (
        <p className="muted" role="status">
          {t("promises.credits.admin.added")}
        </p>
      ) : null}
      <ErrorList error={add.error} />
      <h4>{t("promises.credits.admin.journal")}</h4>
      {rows.length ? (
        <ul className="plain-list">
          {rows.map((row) => (
            <li key={row.id}>
              <strong>
                {row.delta > 0 ? `+${row.delta}` : row.delta} · {t(`promises.credits.admin.reason.${row.reason}`, { defaultValue: row.reason })}
              </strong>{" "}
              <span className="muted">
                → {row.balance} · {row.created_by_name || "—"} · {dateTimeText(row.created_at, i18n.language)}
                {row.note ? ` · ${row.note}` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">{t("promises.credits.admin.empty")}</p>
      )}
    </section>
  );
}
